import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  useDecidedPresentations,
  type DecidedPresentationsFilters,
} from "@/api/presentation-review";
import type { DecidedPresentationItem } from "@/api/types";
import { EmptyState } from "@/components/EmptyState";
import { ErrorBanner } from "@/components/ErrorBanner";
import { RelativeTime } from "@/components/RelativeTime";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatJobNumber } from "@/lib/format";
import { cn } from "@/lib/utils";
import { buildRowTitle } from "./PresentationsPending";

/**
 * `/presentations/decided` — the past-reviews subtab (issue #606).
 * Rendered inside {@link PresentationsLayout}. Companion of
 * {@link PresentationsPending}: same nav, different data view.
 *
 * The operator lands here when they want to revisit a past decision
 * for consistency ("did I approve something like this before?"),
 * auditability, or handoff. The direct-URL escape hatch to
 * `/presentations/:id/review` still exists; this page is the
 * workflow-shaped path back.
 *
 * Filter state (decision multi-select + date range) lives in the URL
 * search params so refresh + back preserve the view. Rows are
 * server-sorted by `decidedAt DESC` with an `id DESC` tiebreak, capped
 * at 200 per response — narrower date ranges are the way past that
 * cap. A subtle "showing latest N" hint appears when the cap bites.
 */

const ALL_DECISIONS = ["approved", "rejected", "expired"] as const;
type Decision = (typeof ALL_DECISIONS)[number];

const RESPONSE_LIMIT = 200;

/**
 * Truncate a free-text preview for the list view (issue #606). The
 * full text remains visible on hover via `title` and on the review
 * page. React text-interpolation keeps the output XSS-safe regardless
 * of what the reviewer typed — see the sibling `MemoDisplay` posture.
 * PII the reviewer chose to paste into notes surfaces here as text;
 * that's the same posture the review page takes.
 */
export function truncateReviewNotes(
  notes: string | null,
  maxChars = 120,
): string {
  if (!notes) return "";
  if (notes.length <= maxChars) return notes;
  return `${notes.slice(0, maxChars - 1)}…`;
}

function parseDecisionsFromSearch(raw: string | null): Decision[] | null {
  if (!raw) return null;
  const tokens = raw
    .split(",")
    .map((t) => t.trim())
    .filter((t): t is Decision => (ALL_DECISIONS as readonly string[]).includes(t));
  if (tokens.length === 0) return null;
  return [...new Set(tokens)];
}

export function PresentationsDecided() {
  const [params, setParams] = useSearchParams();

  const decisionsInUrl = parseDecisionsFromSearch(params.get("decision"));
  // Active decisions default to all three when the URL has no
  // (or an unrecognisable) `decision` param.
  const activeDecisions: readonly Decision[] =
    decisionsInUrl && decisionsInUrl.length > 0
      ? decisionsInUrl
      : ALL_DECISIONS;
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";

  const filters = useMemo<DecidedPresentationsFilters>(() => {
    const f: DecidedPresentationsFilters = {};
    // Only serialise when the operator narrowed — omit when all three
    // are on to keep URLs clean.
    if (
      decisionsInUrl !== null &&
      decisionsInUrl.length !== ALL_DECISIONS.length
    ) {
      f.decision = decisionsInUrl;
    }
    if (from) f.from = from;
    if (to) f.to = toEndOfDay(to);
    return f;
  }, [decisionsInUrl, from, to]);

  const query = useDecidedPresentations(filters);

  const toggleDecision = (d: Decision) => {
    const next = new Set<Decision>(activeDecisions);
    if (next.has(d)) {
      next.delete(d);
    } else {
      next.add(d);
    }
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      // Only three chips → don't let the user disable them all; fall
      // back to "all three" instead of an empty list.
      if (next.size === 0 || next.size === ALL_DECISIONS.length) {
        p.delete("decision");
      } else {
        // Sort so the URL is stable across chip-order variations.
        p.set(
          "decision",
          ALL_DECISIONS.filter((v) => next.has(v)).join(","),
        );
      }
      return p;
    });
  };

  const setDate = (key: "from" | "to", value: string) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      if (value) {
        p.set(key, value);
      } else {
        p.delete(key);
      }
      return p;
    });
  };

  const clearFilters = () => {
    setParams(new URLSearchParams());
  };

  const hasActiveFilters =
    (decisionsInUrl !== null &&
      decisionsInUrl.length !== ALL_DECISIONS.length) ||
    Boolean(from) ||
    Boolean(to);

  return (
    <div className="space-y-6">
      <FilterBar
        activeDecisions={activeDecisions}
        onToggleDecision={toggleDecision}
        from={from}
        to={to}
        onSetDate={setDate}
        hasActiveFilters={hasActiveFilters}
        onClear={clearFilters}
      />

      {query.isPending && <Skeleton className="h-64 w-full" />}
      {query.isError && (
        <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />
      )}
      {query.data !== undefined && !query.isError && (
        <DecidedResults
          rows={query.data.presentations}
          hasActiveFilters={hasActiveFilters}
        />
      )}
    </div>
  );
}

function FilterBar({
  activeDecisions,
  onToggleDecision,
  from,
  to,
  onSetDate,
  hasActiveFilters,
  onClear,
}: {
  activeDecisions: readonly Decision[];
  onToggleDecision: (d: Decision) => void;
  from: string;
  to: string;
  onSetDate: (key: "from" | "to", value: string) => void;
  hasActiveFilters: boolean;
  onClear: () => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-4 rounded-md border border-border p-3">
      <div className="space-y-1">
        <span className="block text-xs font-mono uppercase tracking-label text-text-muted">
          Decision
        </span>
        <div className="flex gap-1">
          {ALL_DECISIONS.map((d) => {
            const on = activeDecisions.includes(d);
            return (
              <button
                key={d}
                type="button"
                aria-pressed={on}
                onClick={() => onToggleDecision(d)}
                className={cn(
                  "inline-flex items-center rounded border px-2 py-1 text-xs font-mono uppercase tracking-label transition-colors",
                  on
                    ? decisionBadgeVariantClass(d, /* on */ true)
                    : "border-border bg-transparent text-text-muted hover:text-text",
                )}
              >
                {d}
              </button>
            );
          })}
        </div>
      </div>
      <div className="space-y-1">
        <label
          htmlFor="decided-from"
          className="block text-xs font-mono uppercase tracking-label text-text-muted"
        >
          From
        </label>
        <input
          id="decided-from"
          type="date"
          value={from}
          onChange={(e) => onSetDate("from", e.target.value)}
          className="rounded-md border border-border bg-bg-card px-3 py-1 text-sm text-text focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </div>
      <div className="space-y-1">
        <label
          htmlFor="decided-to"
          className="block text-xs font-mono uppercase tracking-label text-text-muted"
        >
          To
        </label>
        <input
          id="decided-to"
          type="date"
          value={to}
          onChange={(e) => onSetDate("to", e.target.value)}
          className="rounded-md border border-border bg-bg-card px-3 py-1 text-sm text-text focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </div>
      {hasActiveFilters && (
        <Button variant="outline" size="sm" onClick={onClear}>
          Clear filters
        </Button>
      )}
    </div>
  );
}

function DecidedResults({
  rows,
  hasActiveFilters,
}: {
  rows: DecidedPresentationItem[];
  hasActiveFilters: boolean;
}) {
  if (rows.length === 0) {
    return (
      <EmptyState
        title={hasActiveFilters ? "No decisions match" : "No decisions yet"}
        description={
          hasActiveFilters
            ? "Clear filters or widen the date range to see more."
            : "Approved, rejected, and expired presentations will show up here after decision."
        }
      />
    );
  }

  return (
    <div className="space-y-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Memo</TableHead>
            <TableHead>Client</TableHead>
            <TableHead>Solution type</TableHead>
            <TableHead>Decision</TableHead>
            <TableHead>Decided</TableHead>
            <TableHead>Reviewer</TableHead>
            <TableHead>Notes</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <DecidedRow key={r.id} row={r} />
          ))}
        </TableBody>
      </Table>
      {rows.length === RESPONSE_LIMIT && (
        <p className="text-xs text-text-muted">
          Showing latest {RESPONSE_LIMIT} decisions — narrow the date range to
          see older ones.
        </p>
      )}
    </div>
  );
}

function DecidedRow({ row }: { row: DecidedPresentationItem }) {
  const title = buildRowTitle(row);
  const notesPreview = truncateReviewNotes(row.reviewNotes);
  return (
    <TableRow>
      <TableCell className="font-mono text-xs text-text-muted">
        <Link
          to={`/presentations/${formatJobNumber(row.memoNumber)}/review`}
          className="underline-offset-2 hover:underline"
          title={row.shortId}
        >
          #{formatJobNumber(row.memoNumber)}
        </Link>
      </TableCell>
      <TableCell className="max-w-[220px] truncate" title={title}>
        <Link
          to={`/presentations/${formatJobNumber(row.memoNumber)}/review`}
          className="underline-offset-2 hover:underline"
        >
          {title}
        </Link>
      </TableCell>
      <TableCell className="text-sm">{row.solutionType ?? "—"}</TableCell>
      <TableCell>
        <Badge className={decisionBadgeVariantClass(row.decision, true)}>
          {row.decision}
        </Badge>
      </TableCell>
      <TableCell>
        <RelativeTime date={row.decidedAt} />
      </TableCell>
      <TableCell className="max-w-[180px] truncate text-sm" title={row.reviewedBy ?? ""}>
        {row.reviewedBy ?? "—"}
      </TableCell>
      <TableCell
        className="max-w-[280px] truncate text-sm"
        title={row.reviewNotes ?? ""}
      >
        {notesPreview || "—"}
      </TableCell>
    </TableRow>
  );
}

/**
 * Match the palette to the decision. The `Badge` component's variant
 * enum doesn't have a `warning` slot, so `expired` uses a muted
 * amber-adjacent class inline. All three explicitly styled so the
 * decision column reads at a glance.
 */
function decisionBadgeVariantClass(d: Decision, on: boolean): string {
  if (!on) return "";
  switch (d) {
    case "approved":
      return "border-emerald-500/30 bg-emerald-500/10 text-emerald-300";
    case "rejected":
      return "border-red-500/30 bg-red-500/10 text-red-300";
    case "expired":
      return "border-amber-500/30 bg-amber-500/10 text-amber-300";
  }
}

/**
 * Coerce a `YYYY-MM-DD` date-only string to the end-of-day ISO so the
 * upper-bound filter includes decisions made anywhere within the
 * selected `to` day. Leaves full-ISO datetimes untouched (the parser
 * accepts both shapes).
 *
 * Exported for unit testing.
 */
export function toEndOfDay(dateString: string): string {
  // Cheap heuristic: exactly `YYYY-MM-DD` → append end-of-day suffix.
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return `${dateString}T23:59:59.999Z`;
  }
  return dateString;
}
